import { useEffect, useState } from 'react';
import { api, type FilaHistorial, type Resumen as TResumen } from './api';
import { Metrica, Pastilla, Skeleton, Tarjeta, Vacio, Boton } from './ui';
import { useNavigate } from 'react-router-dom';

/**
 * El resumen.
 *
 * ── EL ORDEN DE LAS MÉTRICAS NO ES ARBITRARIO ───────────────────────────────────
 *
 * 1. Volumen — cuánto se hizo.
 * 2. En revisión — lo ÚNICO que exige acción hoy. Va en ámbar.
 * 3. Costo del mes, con el costo POR PERSONA debajo. El de abajo importa más: es el número
 *    que el cliente compara cuando lo llama la competencia. Tenerlo a la vista es una
 *    decisión comercial, no estética — si no lo mostramos nosotros, se lo calcula él, mal, y
 *    lo discute.
 * 4. Saldo, traducido a verificaciones. Nadie sabe qué son S/ 12.480 en su operación.
 */
export function Resumen({ resumen, nombre }: { resumen: TResumen | null; nombre: string }) {
  const [ultimas, setUltimas] = useState<FilaHistorial[] | null>(null);
  const navegar = useNavigate();

  useEffect(() => {
    api
      .historial()
      .then((f) => setUltimas(f.slice(0, 6)))
      .catch(() => setUltimas([]));
  }, []);

  const sim = simbolo(resumen?.moneda);

  // Saldo → verificaciones. Se usa el costo REAL por persona del cliente, no un promedio
  // inventado: si todavía no verificó a nadie, no hay divisor honesto y no se muestra nada.
  const alcanza =
    resumen && resumen.costoPorPersona && Number(resumen.costoPorPersona) > 0
      ? Math.floor(Number(resumen.saldo) / Number(resumen.costoPorPersona))
      : null;

  return (
    <main className="flex-1 overflow-auto px-8 py-7">
      <header className="mb-6">
        <h1 className="text-[19px] font-bold tracking-[-0.01em]">Hola, {nombre}.</h1>
        <p className="mt-0.5 text-[13px]" style={{ color: 'var(--text-2)' }}>
          {new Date().toLocaleDateString('es-PE', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          })}
        </p>
      </header>

      {!resumen ? (
        <div className="grid grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[92px]" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-3">
          <Metrica
            label="Verificaciones"
            valor={String(resumen.verificaciones)}
            sub="este mes"
          />
          <Metrica
            label="Esperando revisión"
            valor={String(resumen.enRevision)}
            sub={resumen.enRevision > 0 ? 'esperan a una persona' : 'todo resuelto'}
            tono="warn"
          />
          <Metrica
            label="Costo del mes"
            valor={`${sim}${resumen.gastoDelMes}`}
            // `null` cuando no hay verificaciones: no se puede dividir entre cero, y un "0.00"
            // sería un número inventado.
            sub={
              resumen.costoPorPersona
                ? `${sim}${resumen.costoPorPersona} por persona`
                : 'sin verificaciones todavía'
            }
          />
          <Metrica
            label="Saldo"
            valor={`${sim}${resumen.saldo}`}
            sub={alcanza !== null ? `≈ ${alcanza.toLocaleString('es-PE')} verificaciones` : undefined}
          />
        </div>
      )}

      <section className="mt-7">
        <div className="mb-2.5 flex items-baseline justify-between">
          <h2 className="text-[14px] font-semibold">Últimas verificaciones</h2>
          <button
            onClick={() => navegar('/verificaciones')}
            className="text-[12.5px] font-semibold"
            style={{ color: 'var(--accent)' }}
          >
            Ver todas →
          </button>
        </div>

        <Tarjeta className="overflow-hidden">
          {ultimas === null ? (
            <div className="flex flex-col gap-px p-3">
              <Skeleton className="h-8" />
              <Skeleton className="h-8" />
            </div>
          ) : ultimas.length === 0 ? (
            <Vacio
              titulo="Verifica a tu primera persona"
              texto="Escribe un DNI y en un segundo tendrás el veredicto. También puedes integrarlo en tu sistema con dos líneas de código."
              accion={
                <Boton variante="primario" onClick={() => navegar('/verificaciones')}>
                  Verificar a mi primera persona
                </Boton>
              }
            />
          ) : (
            <table className="w-full text-[13px]">
              <tbody>
                {ultimas.map((f) => (
                  <tr key={f.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td className="px-4 py-2.5">{f.nombre ?? 'No encontrada'}</td>
                    <td className="mono px-4 py-2.5 text-[12px]" style={{ color: 'var(--text-3)' }}>
                      {f.documento ?? '····'}
                    </td>
                    <td className="tabular px-4 py-2.5" style={{ color: 'var(--text-3)' }}>
                      {new Date(f.creadaEn).toLocaleString('es-PE', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Pastilla estado={f.estado} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Tarjeta>
      </section>
    </main>
  );
}

function simbolo(moneda: string | undefined): string {
  return moneda === 'PEN' ? 'S/ ' : moneda === 'USD' ? '$' : '';
}
